import { AfterViewInit, ElementRef, EventEmitter, Inject, Input, OnDestroy, Output, PLATFORM_ID, inject, DOCUMENT } from '@angular/core';
import { Directive } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { fromEvent } from 'rxjs/internal/observable/fromEvent';
import { isPlatformBrowser } from '@angular/common';

@Directive({
    selector: '[appDrag]',
    standalone: true,
})
export class DragDirective implements AfterViewInit, OnDestroy {
    private document = inject(DOCUMENT);
    private element: HTMLElement;
    private subscriptions: Subscription[] = [];
    private initialTranslate: number;
    private endTranslate = 0;
    private childWitdh: number;
    private currentTranslate: number;

    @Output() dragging = new EventEmitter<boolean>();
    @Output() currentIndex = new EventEmitter<number>();
    @Input() gap = 25;
    @Input() contentChange: Subject<boolean>;

    private isBrowser = false;

    constructor(@Inject(PLATFORM_ID) platformId: Object, private elementRef: ElementRef) {
        this.isBrowser = isPlatformBrowser(platformId);
    }

    ngAfterViewInit(): void {
        if (this.contentChange) {
            this.contentChange.subscribe(() => {
                this.subscriptions.forEach((x) => x.unsubscribe());
                this.initDrag(this.initialTranslate);
            });
        }

        if (!this.isBrowser) {
            return;
        }
        this.element = this.elementRef.nativeElement as HTMLElement;
        const child = this.element.getElementsByClassName('drag-child')[0];
        this.childWitdh = child.clientWidth / 2;
        const calc = `calc(50% - ${this.childWitdh - this.gap / 2}px)`;
        this.element.style.transform = `translateX(${calc})`;
        this.initDrag();
    }

    initDrag(initialTranslate?: number): void {
        const dragStart$ = fromEvent<MouseEvent>(this.element, 'mousedown');
        const dragEnd$ = fromEvent<MouseEvent>(this.document, 'mouseup');
        const drag$ = fromEvent<MouseEvent>(this.document, 'mousemove').pipe(takeUntil(dragEnd$));

        this.initialTranslate = new DOMMatrixReadOnly(window.getComputedStyle(this.element).getPropertyValue('transform')).m41;
        if (initialTranslate) {
            this.initialTranslate = initialTranslate;
            this.element.style.transform = 'translateX(' + initialTranslate + 'px)';
        }

        let initialX: number;
        let currentX = 0;
        let t: number;

        let dragSub: Subscription = new Subscription();
        const dragStartSub = dragStart$.subscribe((dEvent: MouseEvent) => {
            initialX = dEvent.clientX - currentX + this.endTranslate;
            this.element.style.transition = 'none';

            dragSub = drag$.subscribe((event: MouseEvent) => {
                this.dragging.next(true);
                event.preventDefault();
                currentX = event.clientX - initialX;
                t = currentX + this.initialTranslate;

                this.element.style.transform = 'translateX(' + t + 'px)';
            });
        });
        const dragEndSub = dragEnd$.subscribe(() => {
            initialX = currentX;
            this.centerItems();
            if (dragSub) {
                dragSub.unsubscribe();
            }
            this.dragging.next(false);
        });

        import('hammerjs').then((h) => {
            const hammerTime = new h.default(this.element);
            let direction = 1;
            hammerTime.on('panstart', (ev: any) => {
                this.dragging.next(true);
                if (ev.pointerType !== 'touch') {
                    return;
                }
                this.currentTranslate = new DOMMatrixReadOnly(window.getComputedStyle(this.element).getPropertyValue('transform')).m41;
                initialX = ev.deltaX - currentX + this.endTranslate;
                this.element.style.transition = 'none';
            });

            hammerTime.on('panend', (ev: any) => {
                if (ev.pointerType !== 'touch') {
                    return;
                }
                initialX = currentX;
                this.centerItems(direction);
                this.dragging.next(false);
            });

            hammerTime.on('swipeleft', (ev: any) => {
                direction = 1;
            });

            hammerTime.on('swiperight', (ev: any) => {
                direction = -1;
            });

            hammerTime.on('pan', (ev: any) => {
                if (ev.pointerType !== 'touch') {
                    return;
                }
                this.dragging.next(true);
                ev.preventDefault();
            });

            this.subscriptions.push.apply(this.subscriptions, [dragStartSub, dragSub, dragEndSub]);
        });
    }

    centerItems(direction?: number): void {
        this.element.style.transition = '0.3s transform ease-in-out';
        const center = this.element.clientWidth / 2;
        const children = this.element.getElementsByClassName('drag-child');
        const translate = new DOMMatrixReadOnly(window.getComputedStyle(this.element).getPropertyValue('transform')).m41;
        let nTranslate = 0;
        let index: number = -1;
        if (!direction) {
            for (let i = 0; i < children.length; i++) {
                const child = children[i] as HTMLElement;
                const offset = child.offsetLeft + translate;
                const childWidth = offset + child.clientWidth + this.gap;

                if (center < childWidth && i === 0) {
                    nTranslate = center - child.offsetLeft - child.clientWidth / 2;
                    index = i;
                    break;
                }

                if ((center > offset && center < childWidth) || i === children.length - 1) {
                    nTranslate = center - child.offsetLeft - child.clientWidth / 2;
                    index = i;
                    break;
                }
            }
        } else {
            let nextChild;
            for (let i = 0; i < children.length; i++) {
                const child = children[i] as HTMLElement;
                const offset = child.offsetLeft + this.currentTranslate;
                const childWidth = offset + child.clientWidth + this.gap;

                if (center < childWidth && i === 0) {
                    if (direction === -1) {
                        nextChild = child;
                        index = i;
                    } else {
                        nextChild = children[i + direction] as HTMLElement;
                        index = i + direction;
                    }
                    break;
                }

                if ((center > offset && center < childWidth) || i === children.length - 1) {
                    if (i === children.length - 1 && direction === 1) {
                        nextChild = child;
                        index = i;
                    } else {
                        nextChild = children[i + direction] as HTMLElement;
                        index = i + direction;
                    }
                    break;
                }
            }
            if (nextChild) {
                nTranslate = center - nextChild?.offsetLeft - nextChild?.clientWidth / 2;
            }
        }

        this.currentIndex.next(index);

        nTranslate = nTranslate + this.gap / 2;
        this.endTranslate = translate - nTranslate;
        this.element.style.transform = 'translateX(' + nTranslate + 'px)';
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }
}
